import { AfterViewInit, ChangeDetectorRef, Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { DecimalPipe } from '@angular/common';

import { AppConfig } from 'src/app/app.config';
import {
    CalculationCompound,
    HighestRiskCalculationType,
    LocationItem, LocationSearchCacheKey, LocationSearchOrder,
    LocationSearchType, RiskAssessmentStatus, ScreeningLocationType, ScreeningPolygonStatus
} from 'src/app/contracts';
import { CompoundService, LocationService } from 'src/app/services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NumberOrDefaultPipe } from 'src/app/shared/pipes/number-or-default.pipe';

class StatusFilter {
    public status: ScreeningPolygonStatus;
    public calculationType: HighestRiskCalculationType;
    public text: string;
}
class LocationTypeFilter {
    public status: ScreeningLocationType;
    public text: string;
}

@Component({
    selector: 'app-search-location',
    templateUrl: './search-location.component.html',
    styleUrls: ['./search-location.component.css']
})

export class ScreeningSearchLocationComponent implements OnInit, AfterViewInit, OnDestroy {
    public displaySearchResults = false;
    public isSearching = false;
    public isContinuing = false;
    public initialSearch = true;
    public hasMoreSearchResults = false;
    public searchOffset = 0;
    public loading = true;

    public searchText = '';
    public minScreeningFactor = 0;
    public maxScreeningFactor = null;
    // Make enums accessible from the html-template
    public riskAssessmentStatus = RiskAssessmentStatus;
    public screeningLocationType = ScreeningLocationType;
    public locationSearchOrder = LocationSearchOrder;
    public screeningPolygonStatus = ScreeningPolygonStatus;
    public highestRiskCalculationType = HighestRiskCalculationType;

    public orderBy = LocationSearchOrder.ScreeningFactor;
    public orderDescending = true;
    public searchResults: Array<LocationItem>;

    public locationTypeFilters: LocationTypeFilter[] = [
        {
            status: ScreeningLocationType.V1,
            text: 'V1'
        },
        {
            status: ScreeningLocationType.V2,
            text: 'V2'
        },
        {
            status: ScreeningLocationType.V1_V2,
            text: 'V1/V2'
        }
    ];
    public statusFilters: StatusFilter[] = [
        {
            status: ScreeningPolygonStatus.NoRisk,
            calculationType: HighestRiskCalculationType.None,
            text: 'Ingen risiko'
        },
        {
            status: ScreeningPolygonStatus.Risk,
            calculationType: HighestRiskCalculationType.None,
            text: 'Risiko'
        },
        {
            status: ScreeningPolygonStatus.Risk,
            calculationType: HighestRiskCalculationType.Screening,
            text: 'Risiko (V1)'
        }
    ];
    public statusFilter: StatusFilter;
    public locationTypeFilter: LocationTypeFilter;
    public filteredSearchResults: Array<LocationItem>;
    public compoundList: CalculationCompound[];
    public filteredCompoundList: CalculationCompound[];
    public contaminantName: string;

    protected destroyed$ = new Subject<void>();

    constructor(
        @Inject(LOCALE_ID) private readonly locale,
        private readonly locationService: LocationService,
        private readonly compoundService: CompoundService,
        private readonly cdref: ChangeDetectorRef,
        private readonly numberOrDefaultPipe: NumberOrDefaultPipe) {
    }

    public ngOnInit(): void {
        const searchView = this.locationService.getLocationsFromCache(LocationSearchCacheKey.Screening);
        if (searchView.total === 0) {
            return;
        }
        this.searchOffset = searchView.items.length;
        this.searchResults = searchView.items;
        this.filteredSearchResults = searchView.items;

        this.isSearching = false;
        this.initialSearch = false;
        this.isContinuing = false;
        this.displaySearchResults = searchView.items.length > 0;
        this.hasMoreSearchResults = this.searchOffset + AppConfig.settings.maxItems < searchView.total;
    }

    public ngAfterViewInit(): void {
        this.getScreeningCompounds();
        this.onSearch();
        this.cdref.detectChanges();
    }

    public getScreeningCompounds(): void {
        const compounds = this.compoundService.getScreeningCompoundsFromCache(LocationSearchCacheKey.ScreeningCompound);
        if (!compounds || compounds.length === 0) {
            this.compoundService.getScreeningCompounds()
                .pipe(takeUntil(this.destroyed$))
                .subscribe(res => {
                    this.compoundList = res;
                    this.compoundService.setScreeningCompoundsFromCache(LocationSearchCacheKey.ScreeningCompound, res);
                    this.filteredCompoundList = [...res];
                    this.loading = false;
                });
        } else {
            this.compoundList = compounds;
            this.filteredCompoundList = compounds;
            this.loading = false;
        }
    }

    public getLocationTypeString(type: ScreeningLocationType): string {
        if (type === ScreeningLocationType.V1) {
            return 'V1';
        }
        if (type === ScreeningLocationType.V2) {
            return 'V2';
        }
        if (type === ScreeningLocationType.V1_V2) {
            return 'V1/V2';
        }
        return '-';
    }

    public setOrderBy(newOrderBy: LocationSearchOrder): void {
        if (this.orderBy === newOrderBy) {
            this.orderDescending = !this.orderDescending;
        }
        else {
            this.orderDescending = false;
        }
        this.orderBy = newOrderBy;
        this.searchOffset = 0;
        this.refreshSearch();
    }

    public refreshSearch(continueSearch: boolean = false): void {
        if (this.isSearching) {
            return;
        }
        this.isSearching = true;
        this.isContinuing = continueSearch;
        // // this.displaySearchResults = continueSearch;
        this.hasMoreSearchResults = false;
        if (continueSearch) {
            this.searchOffset += AppConfig.settings.maxItems;
        }
        this.checkFilterStatus();

        const criteria = {
            searchText: this.searchText,
            caseWorkerName: '',
            riskAssessmentStatus: null,
            calculationType: this.statusFilter,
            searchType: LocationSearchType.Screening,
            minScreeningFactor: this.minScreeningFactor,
            maxScreeningFactor: this.maxScreeningFactor,
            screeningContaminant: this.contaminantName,
            polygonStatus: this.statusFilter,
            polygonType: this.locationTypeFilter,
            orderBy: this.orderBy,
            orderDescending: this.orderDescending,
            skip: this.searchOffset,
            take: AppConfig.settings.maxItems
        };
        this.locationService.search(criteria)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((res) => {
                res.items.forEach(p => {
                    if (p.screeningFactor === Infinity || isNaN(p.screeningFactor)) {
                        p.screeningFactor = 0;
                    }
                });
                if (continueSearch) {
                    this.searchResults = this.searchResults.concat(res.items);
                }
                else {
                    this.searchResults = res.items;
                }
                this.isSearching = false;
                this.initialSearch = false;
                this.isContinuing = false;
                this.displaySearchResults = res.items.length > 0;
                this.hasMoreSearchResults = this.searchOffset + AppConfig.settings.maxItems < res.total;

                res.items = this.searchResults;
                this.locationService.setLocationsToCache(LocationSearchCacheKey.Screening, res);
            });
    }

    public onSearch(): void {
        this.searchOffset = 0;
        this.orderBy = LocationSearchOrder.ScreeningFactor;
        this.orderDescending = true;
        this.refreshSearch();
    }

    public onKeydown(event: any): void {
        this.onSearch();
    }

    public getMoreResults(): void {
        this.refreshSearch(true);
    }

    public getNumberWithSignificantDigits(val: number): string {
        const n = this.numberOrDefaultPipe.transform(val);
        let digitsInfo = '';
        if (n >= 10.0) // xx.x
        {
            digitsInfo = '1.1-1';
        }
        else if (n >= 1.0) // x.xx
        {
            digitsInfo = '1.2-2';
        }
        else if (n >= 0.1) // 0.xxx
        {
            digitsInfo = '1.3-3';
        }
        else if (n >= 0.01) // 0.0xxx
        {
            digitsInfo = '1.4-4';
        }
        else // 0.00xxx
        {
            digitsInfo = '1.5-5';
        }

        const result = new DecimalPipe(this.locale).transform(n, digitsInfo);
        return result;
    }

    public filterContaminants(contaminantName: string): void {
        if (!contaminantName) {
            this.filteredCompoundList = this.compoundList;
        } else {
            this.filteredCompoundList = this.compoundList.filter(c => c.name.toLowerCase().indexOf(contaminantName.toLowerCase()) >= 0);
        }
        if (!this.contaminantName || this.contaminantName.trim().length === 0){
            this.refreshSearch();
        }
    }

    public checkFilterStatus(): void{
        if (this.statusFilter){
            if (this.statusFilter.status === ScreeningPolygonStatus.Risk
                && this.statusFilter.calculationType === HighestRiskCalculationType.Screening){
                this.locationTypeFilter = this.locationTypeFilters[0];
            }
        }
    }

    public ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}
